import { AlertCircle, Calendar, DollarSign, Edit, Filter, Plus, Search, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import useServices from '../hooks/useServices';
import './PreciosEstacionales.css';

const PreciosEstacionales = () => {
  const { seasonService, seasonPriceService, vehicleModelService } = useServices();
  const [seasonPrices, setSeasonPrices] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [vehicleModels, setVehicleModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [filters, setFilters] = useState({
    seasonId: '',
    vehicleModelId: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [formData, setFormData] = useState({
    vehicleModelId: '',
    seasonId: '',
    basePrice: '',
    threeToFive: 15,
    additionalDay: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadSeasonPrices();
    setPagination(prev => ({ ...prev, currentPage: 1 })); // Resetear a página 1 cuando cambien los filtros
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [seasonsData, modelsData] = await Promise.all([
        seasonService.getAllSeasons(),
        vehicleModelService.getAllVehicleModels()
      ]);
      
      setSeasons(seasonsData);
      setVehicleModels(modelsData);
      setError(null);
    } catch (err) {
      setError('Error al cargar los datos');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSeasonPrices = async () => {
    try {
      const data = await seasonPriceService.getAllSeasonPrices(filters);
      setSeasonPrices(data);
    } catch (err) {
      setError('Error al cargar los precios estacionales');
      console.error('Error loading season prices:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const priceData = {
        ...formData,
        basePrice: parseFloat(formData.basePrice),
        threeToFive: parseFloat(formData.threeToFive),
        additionalDay: parseFloat(formData.additionalDay)
      };

      if (editingPrice) {
        await seasonPriceService.updateSeasonPrice(editingPrice.id, priceData);
      } else {
        await seasonPriceService.createSeasonPrice(priceData);
      }
      
      await loadSeasonPrices();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar el precio estacional');
      console.error('Error saving season price:', err);
    }
  };

  const handleEdit = (price) => {
    setEditingPrice(price);
    setFormData({
      vehicleModelId: price.vehicleModelId.toString(),
      seasonId: price.seasonId.toString(),
      basePrice: price.basePrice.toString(),
      threeToFive: price.threeToFive.toString(),
      additionalDay: price.additionalDay.toString()
    });
    setShowForm(true);
  };

  const handleDelete = async (price) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar este precio estacional?`)) {
      try {
        await seasonPriceService.deleteSeasonPrice(price.id);
        await loadSeasonPrices();
      } catch (err) {
        setError(err.response?.data?.error || 'Error al eliminar el precio estacional');
        console.error('Error deleting season price:', err);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      vehicleModelId: '',
      seasonId: '',
      basePrice: '',
      threeToFive: 15,
      additionalDay: ''
    });
    setEditingPrice(null);
    setShowForm(false);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Funciones de paginación
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setPagination(prev => ({
      ...prev,
      itemsPerPage: parseInt(newItemsPerPage),
      currentPage: 1
    }));
  };

  const getPaginationInfo = () => {
    const startItem = (pagination.currentPage - 1) * pagination.itemsPerPage + 1;
    const endItem = Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems);
    return { startItem, endItem };
  };

  // Filtrar precios localmente (búsqueda por texto)
  const filteredPrices = seasonPrices.filter(price => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      return (
        price.vehicleModel.name.toLowerCase().includes(searchTerm) ||
        price.season.name.toLowerCase().includes(searchTerm) ||
        price.vehicleModel.category.name.toLowerCase().includes(searchTerm)
      );
    }
    return true;
  });

  // Calcular paginación
  const totalItems = filteredPrices.length;
  const totalPages = Math.ceil(totalItems / pagination.itemsPerPage);
  const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
  const endIndex = startIndex + pagination.itemsPerPage;
  const paginatedPrices = filteredPrices.slice(startIndex, endIndex);

  // Actualizar paginación cuando cambien los datos
  React.useEffect(() => {
    setPagination(prev => ({
      ...prev,
      totalItems,
      totalPages,
      currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage
    }));
  }, [totalItems, totalPages]);

  const getSeasonName = (seasonId) => {
    const season = seasons.find(s => s.id === seasonId);
    return season ? season.name : 'Temporada no encontrada';
  };

  const getModelName = (modelId) => {
    const model = vehicleModels.find(m => m.id === modelId);
    return model ? model.name : 'Modelo no encontrado';
  };

  const getCategoryName = (modelId) => {
    const model = vehicleModels.find(m => m.id === modelId);
    return model ? model.category.name : 'Categoría no encontrada';
  };

  if (loading) {
    return (
      <div className="precios-estacionales-container">
        <div className="loading">Cargando precios estacionales...</div>
      </div>
    );
  }

  return (
    <div className="precios-estacionales-container">
      <div className="precios-estacionales-header">
        <h1>Precios Estacionales</h1>
        <button 
          className="btn btn-primary"
          onClick={() => setShowForm(true)}
        >
          <Plus size={20} />
          Nuevo Precio
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Filtros */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label htmlFor="seasonFilter">Filtrar por temporada</label>
            <select
              id="seasonFilter"
              value={filters.seasonId}
              onChange={(e) => handleFilterChange('seasonId', e.target.value)}
            >
              <option value="">Todas las temporadas</option>
              {seasons.map(season => (
                <option key={season.id} value={season.id}>
                  {season.name} ({season.year})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="modelFilter">Filtrar por modelo</label>
            <select
              id="modelFilter"
              value={filters.vehicleModelId}
              onChange={(e) => handleFilterChange('vehicleModelId', e.target.value)}
            >
              <option value="">Todos los modelos</option>
              {vehicleModels.map(model => (
                <option key={model.id} value={model.id}>
                  {model.name} ({model.category.name})
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="search">Buscar</label>
            <div className="search-input">
              <Search size={20} />
              <input
                type="text"
                id="search"
                placeholder="Buscar por modelo, temporada o categoría..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Formulario Modal */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingPrice ? 'Editar Precio Estacional' : 'Nuevo Precio Estacional'}</h2>
              <button 
                className="btn-close"
                onClick={resetForm}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="price-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="seasonId">Temporada</label>
                  <select
                    id="seasonId"
                    value={formData.seasonId}
                    onChange={(e) => setFormData({...formData, seasonId: e.target.value})}
                    required
                  >
                    <option value="">Seleccionar temporada</option>
                    {seasons.map(season => (
                      <option key={season.id} value={season.id}>
                        {season.name} ({season.year})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="vehicleModelId">Modelo de Vehículo</label>
                  <select
                    id="vehicleModelId"
                    value={formData.vehicleModelId}
                    onChange={(e) => setFormData({...formData, vehicleModelId: e.target.value})}
                    required
                  >
                    <option value="">Seleccionar modelo</option>
                    {vehicleModels.map(model => (
                      <option key={model.id} value={model.id}>
                        {model.name} ({model.category.name})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="basePrice">Precio Base (€)</label>
                  <input
                    type="number"
                    id="basePrice"
                    value={formData.basePrice}
                    onChange={(e) => setFormData({...formData, basePrice: e.target.value})}
                    step="0.01"
                    min="0"
                    required
                    placeholder="0.00"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="threeToFive">Descuento 3-5 días (%)</label>
                  <input
                    type="number"
                    id="threeToFive"
                    value={formData.threeToFive}
                    onChange={(e) => setFormData({...formData, threeToFive: e.target.value})}
                    step="0.1"
                    min="0"
                    max="100"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="additionalDay">Precio Día Adicional (€)</label>
                <input
                  type="number"
                  id="additionalDay"
                  value={formData.additionalDay}
                  onChange={(e) => setFormData({...formData, additionalDay: e.target.value})}
                  step="0.01"
                  min="0"
                  required
                  placeholder="0.00"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingPrice ? 'Actualizar' : 'Crear'} Precio
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Información de paginación y controles */}
      <div className="pagination-controls">
        <div className="pagination-info">
          <span>
            Mostrando {getPaginationInfo().startItem} - {getPaginationInfo().endItem} de {pagination.totalItems} precios
          </span>
        </div>
        
        <div className="pagination-controls-right">
          <div className="items-per-page">
            <label htmlFor="itemsPerPage">Elementos por página:</label>
            <select
              id="itemsPerPage"
              value={pagination.itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(e.target.value)}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabla de precios */}
      <div className="prices-table-container">
        {paginatedPrices.length === 0 ? (
          <div className="empty-state">
            <DollarSign size={48} />
            <h3>No hay precios estacionales</h3>
            <p>Crea tu primer precio estacional para comenzar a gestionar precios por temporada.</p>
          </div>
        ) : (
          <table className="prices-table">
            <thead>
              <tr>
                <th>Modelo</th>
                <th>Categoría</th>
                <th>Temporada</th>
                <th>Precio Base</th>
                <th>Descuento 3-5 días</th>
                <th>Día Adicional</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginatedPrices.map((price) => (
                <tr key={price.id}>
                  <td>
                    <div className="model-info">
                      <strong>{getModelName(price.vehicleModelId)}</strong>
                    </div>
                  </td>
                  <td>
                    <span className="category-badge">
                      {getCategoryName(price.vehicleModelId)}
                    </span>
                  </td>
                  <td>
                    <div className="season-info">
                      <strong>{getSeasonName(price.seasonId)}</strong>
                    </div>
                  </td>
                  <td>
                    <span className="price-value">€{price.basePrice.toFixed(2)}</span>
                  </td>
                  <td>
                    <span className="discount-value">{price.threeToFive}%</span>
                  </td>
                  <td>
                    <span className="price-value">€{price.additionalDay.toFixed(2)}</span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => handleEdit(price)}
                        title="Editar"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(price)}
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Controles de paginación */}
      {pagination.totalPages > 1 && (
        <div className="pagination-navigation">
          <div className="pagination-buttons">
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => handlePageChange(1)}
              disabled={pagination.currentPage === 1}
            >
              Primera
            </button>
            
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
            >
              Anterior
            </button>

            {/* Números de página */}
            <div className="page-numbers">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                let pageNum;
                if (pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (pagination.currentPage <= 3) {
                  pageNum = i + 1;
                } else if (pagination.currentPage >= pagination.totalPages - 2) {
                  pageNum = pagination.totalPages - 4 + i;
                } else {
                  pageNum = pagination.currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className={`btn btn-sm ${pageNum === pagination.currentPage ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              className="btn btn-sm btn-secondary"
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              Siguiente
            </button>
            
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              Última
            </button>
          </div>
          
          <div className="pagination-summary">
            Página {pagination.currentPage} de {pagination.totalPages}
          </div>
        </div>
      )}
    </div>
  );
};

export default PreciosEstacionales;
